import { useState, useEffect, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin } from "lucide-react";
import type { PublicProperty, NearbyAttraction } from "@shared/schema";
import { SectionHeading } from "@/components/ui/section-heading";
import { AttractionLightbox } from "./attraction-lightbox";
import { useAuth } from "@/hooks/use-auth";
import type { MapInnerProps } from "./location-map-inner";
import { LazyVideo } from "@/components/ui/lazy-video";
import { wavesVideo, wavesPoster } from "@/lib/media-assets";

export function LocationMap({ propertySlug }: { propertySlug?: string } = {}) {
  const scope = propertySlug ? { property: propertySlug } : undefined;
  const [mapError, setMapError] = useState<string | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState<NearbyAttraction | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [MapInner, setMapInner] = useState<ComponentType<MapInnerProps> | null>(null);
  const { user } = useAuth();

  const { data: property } = useQuery<PublicProperty>({
    queryKey: scope ? ["/api/property/public", scope] : ["/api/property/public"],
  });

  const { data: exactCoordinates } = useQuery<{ lat: string; lng: string } | null>({
    queryKey: scope ? ["/api/property/coordinates", scope] : ["/api/property/coordinates"],
    enabled: !!user,
    retry: false,
  });

  const { data: nearbyAttractions = [] } = useQuery<NearbyAttraction[]>({
    queryKey: scope ? ["/api/nearby-attractions", scope] : ["/api/nearby-attractions"],
  });

  const handleAttractionClick = (attraction: NearbyAttraction) => {
    setSelectedAttraction(attraction);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedAttraction(null);
  };

  const handleMapLoad = () => setIsMapLoaded(true);
  const handleMapError = (error: any) => {
    console.error('Map loading error:', error);
    setMapError('Failed to load map. Please check your internet connection.');
  };
  const handleRetry = () => {
    setMapError(null);
    setIsMapLoaded(false);
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-property-marker {
        position: relative;
        color: var(--ink);
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
      }
      .marker-pulse {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 60px;
        height: 60px;
        background: var(--ink);
        border-radius: 50%;
        opacity: 0.2;
        animation: pulse 2s infinite;
      }
      .marker-icon {
        position: relative;
        z-index: 10;
        transition: transform 0.2s ease;
      }
      .custom-property-marker:hover .marker-icon { transform: scale(1.1); }
      .custom-attraction-marker {
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
        transition: transform 0.2s ease;
      }
      .custom-attraction-marker:hover { transform: scale(1.1); }
      @keyframes pulse {
        0%   { opacity: 0.2; transform: translate(-50%, -50%) scale(0.8); }
        50%  { opacity: 0.05; transform: translate(-50%, -50%) scale(1.2); }
        100% { opacity: 0.2; transform: translate(-50%, -50%) scale(0.8); }
      }
      .leaflet-container { border-radius: 4px; overflow: hidden; border: 1px solid var(--sand-deep); }
      .leaflet-popup-content-wrapper { border-radius: 4px; border: 1px solid var(--sand-deep); background: var(--shell); box-shadow: none; }
      .leaflet-popup-content { margin: 16px; font-family: var(--font-sans); color: var(--ink); }
      .leaflet-control-zoom { border: 1px solid var(--sand-deep) !important; border-radius: 4px !important; overflow: hidden; box-shadow: none !important; }
      .leaflet-control-zoom a { background: var(--shell) !important; color: var(--ink) !important; border: none !important; font-weight: 400; transition: all 0.2s ease; }
      .leaflet-control-zoom a:hover { background: var(--sand) !important; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (!isMapLoaded) return;
    const fixMarkerAccessibility = () => {
      document.querySelectorAll('.leaflet-marker-icon[role="button"]').forEach((el) => {
        if (el.getAttribute('aria-label')) return;
        const innerLabel = el.querySelector('[aria-label]');
        if (innerLabel) {
          el.setAttribute('aria-label', innerLabel.getAttribute('aria-label') || 'Map marker');
        } else if (el.classList.contains('custom-attraction-wrapper')) {
          const d = el.querySelector('.custom-attraction-marker');
          el.setAttribute('aria-label', d?.getAttribute('aria-label') || 'Nearby attraction');
        } else if (el.classList.contains('custom-marker-wrapper')) {
          const d = el.querySelector('.custom-property-marker');
          el.setAttribute('aria-label', d?.getAttribute('aria-label') || 'Property location');
        } else {
          el.setAttribute('aria-label', 'Map marker');
        }
      });
    };
    fixMarkerAccessibility();
    const t1 = setTimeout(fixMarkerAccessibility, 500);
    const t2 = setTimeout(fixMarkerAccessibility, 1500);
    const observer = new MutationObserver(() => fixMarkerAccessibility());
    const mapContainer = document.querySelector('.leaflet-container');
    if (mapContainer) observer.observe(mapContainer, { childList: true, subtree: true });
    return () => { clearTimeout(t1); clearTimeout(t2); observer.disconnect(); };
  }, [isMapLoaded, nearbyAttractions, property]);

  const [mapChunkFailed, setMapChunkFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = (attempt = 0) => {
      import('./location-map-inner')
        .then((m) => {
          if (!cancelled) setMapInner(() => m.MapInner);
        })
        .catch((err) => {
          console.error('Map component failed to load (attempt ' + attempt + '):', err);
          if (attempt < 2) {
            setTimeout(() => load(attempt + 1), 700 * (attempt + 1));
          } else if (!cancelled) {
            setMapChunkFailed(true);
          }
        });
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <section id="location" className="py-20 bg-shell border-t border-sand-deep section-stable">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <SectionHeading
            align="center"
            eyebrow="Location"
            title="Where you'll be"
            lede="Prime oceanfront location on Oahu's scenic windward coast"
            className="mb-12"
          />

          {/* The map column stretches to the full row so the map inside it can stick
              while the attractions list scrolls past. */}
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="order-2 lg:order-1">
              <div className="lg:sticky lg:top-28">
              <Card className="overflow-hidden border-sand-deep rounded-sm bg-sand shadow-none">
                {MapInner ? (
                  <MapInner
                    property={property}
                    nearbyAttractions={nearbyAttractions}
                    exactCoordinates={exactCoordinates}
                    isMapLoaded={isMapLoaded}
                    mapError={mapError}
                    user={user}
                    onMapLoad={handleMapLoad}
                    onMapError={handleMapError}
                    onRetry={handleRetry}
                    onAttractionClick={handleAttractionClick}
                  />
                ) : mapChunkFailed ? (
                  <div className="h-[500px] md:h-[600px] bg-gradient-to-br from-shell to-sand flex items-center justify-center">
                    <div className="text-center px-6">
                      <MapPin className="h-8 w-8 text-palm mx-auto mb-3" aria-hidden="true" />
                      <h3 className="text-luxury-lg font-serif font-normal text-foreground mb-2">Map didn't load</h3>
                      <p className="text-muted-foreground mb-4 text-luxury-sm">Ka'a'awa, Oahu, directly across from Kualoa Ranch on the windward coast.</p>
                      <Button onClick={() => window.location.reload()} variant="outline" size="sm">Reload map</Button>
                    </div>
                  </div>
                ) : (
                  <div className="h-[500px] md:h-[600px] bg-sand flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-pulse space-y-3">
                        <div className="h-4 w-48 bg-sand-deep rounded mx-auto" />
                        <div className="h-3 w-32 bg-sand-deep rounded mx-auto" />
                      </div>
                    </div>
                  </div>
                )}
              </Card>
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-12">
              <div>
                <h3 className="text-luxury-2xl font-serif font-normal text-forest mb-4 tracking-tight">Ka'a'awa, Oahu</h3>
                <p className="text-ink-soft mb-6 leading-relaxed font-light text-luxury-lg">
                  Located directly across from world-famous Kualoa Ranch on Oahu's stunning windward coast.
                  This oceanfront location offers incredible views of Mokoli'i (Chinaman's Hat) and puts you
                  just steps away from movie filming locations, pristine beaches, and authentic Hawaiian culture.
                </p>
              </div>

              <div className="space-y-6">
                <h4 className="text-luxury-xl font-serif font-normal text-forest tracking-tight">Nearby Attractions</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {nearbyAttractions.length > 0 ? (
                    nearbyAttractions.map((attraction) => (
                      <button
                        key={attraction.id}
                        onClick={() => handleAttractionClick(attraction)}
                        className="group relative p-5 bg-sand border border-sand-deep border-l-[3px] border-l-palm/40 rounded-sm hover:border-l-palm hover:to-sand transition-all duration-300 cursor-pointer text-left shadow-none"
                        data-testid={`attraction-${attraction.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-palm/10">
                            <MapPin className="h-3.5 w-3.5 text-palm" aria-hidden="true" />
                          </span>
                          <div className="space-y-1">
                            <p className="font-serif font-medium text-ink text-luxury-base group-hover:text-ink transition-colors tracking-tight">
                              {attraction.name}
                            </p>
                            <p className="text-luxury-xs text-ink-soft font-light">
                              {attraction.distance || "N/A"}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-8 border border-dashed border-sand-deep rounded-sm">
                      <p className="text-ink-soft font-sans font-light">No nearby attractions configured</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="relative overflow-hidden rounded-sm text-ink bg-shell border border-sand-deep">
                <div className="relative h-48">
                  <LazyVideo
                    src={wavesVideo}
                    poster={wavesPoster}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                    aria-label="Idyllic Oahu beachfront location with pristine sands and azure ocean - perfect vacation rental setting near Mokoli'i (Chinaman's Hat) and Kualoa Ranch"
                  />
                </div>
                <div className="relative p-8 flex flex-col justify-end">
                  <h4 className="font-serif font-normal mb-3 text-forest text-luxury-2xl tracking-tight">Perfect Location</h4>
                  <ul className="text-ink-soft text-luxury-sm space-y-2 font-light tracking-wide">
                    <li><span className="text-sun mr-2">•</span>Direct ocean views of Mokoli'i (Chinaman's Hat)</li>
                    <li><span className="text-sun mr-2">•</span>1-minute walk to Kualoa Ranch (Jurassic Park filming site)</li>
                    <li><span className="text-sun mr-2">•</span>Secluded windward coast location</li>
                    <li><span className="text-sun mr-2">•</span>Gateway to North Shore and cultural attractions</li>
                  </ul>
                </div>
              </div>

              <Button
                className="bg-ink text-shell hover:bg-ink/90 font-sans font-medium tracking-wide shadow-none border-none py-3 px-8 rounded-sm w-full sm:w-auto"
                data-testid="explore-area-button"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Explore the area
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AttractionLightbox
        attraction={selectedAttraction}
        isOpen={isLightboxOpen}
        onClose={handleLightboxClose}
      />
    </section>
  );
}
